"use client";

import React from "react";
import { Mail, MapPin, Building2, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const inputBaseClasses = "flex w-full min-w-0 rounded-none border-b border-input bg-transparent px-0 py-2 text-base shadow-none transition-all outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-primary focus-visible:ring-0 aria-invalid:border-destructive placeholder:text-muted-foreground/50";
function PartnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="223576" className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-primary/5 border border-primary/10 rounded-sm">
        <CircleCheck className="size-12 text-primary" />
        <div className="space-y-2">
          <h3 className="text-xl font-default font-semibold text-foreground">Inquiry Received</h3>
          <p className="text-muted-foreground font-default">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-none border-primary text-primary hover:bg-primary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-default text-secondary">Full Name</FormLabel>
              <FormControl>
                <Input className={inputBaseClasses} placeholder="Alexander Vance" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Please enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-default text-secondary">Corporate Email</FormLabel>
              <FormControl>
                <Input className={inputBaseClasses} type="email" placeholder="vance@global-ops.com" {...field} />
              </FormControl>
              <FormDescription className="sr-only">We'll use this to contact you about the project.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company_name" rules={{
        required: "Organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-default text-secondary">Organization</FormLabel>
              <FormControl>
                <Input className={inputBaseClasses} placeholder="Nova Logistics Group" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Name of the business you represent.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-default text-secondary">Direct Line <span className="text-muted-foreground/50 font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input className={inputBaseClasses} type="tel" placeholder="+44 20 7946 0123" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Optional – a direct line helps us reach you faster.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="partnership_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider font-default text-secondary">Inquiry Type</FormLabel>
            <FormControl>
              <select className={`${inputBaseClasses} h-10 appearance-none`} {...field}>
                <option value="" disabled>Select service area</option>
                <option value="Project Management">Project Management</option>
                <option value="Global Logistics">Global Logistics</option>
                <option value="Strategic Consulting">Strategic Consulting</option>
                <option value="Administrative Support">Administrative Support</option>
              </select>
            </FormControl>
            <FormDescription className="sr-only">Choose the service area that best fits your needs.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider font-default text-secondary">Project Brief <span className="text-muted-foreground/50 font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <textarea className={`${inputBaseClasses} min-h-[100px] py-2 resize-none`} placeholder="Outline your global project requirements..." {...field} />
            </FormControl>
            <FormDescription className="sr-only">Provide any specific goals, expectations, or questions.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-bold rounded-none bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : 'Initiate Consultation'}
      </Button>
    </Form>;
}
export default function PartnerWithJadav() {
  return <section id="start-a-conversation" className="bg-background text-foreground py-24 px-6 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/5 blur-[140px] rounded-full pointer-events-none" />
      
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start relative z-10">
        
        {}
        <div className="space-y-10 lg:sticky lg:top-24">
          <div className="space-y-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-foreground">
              Start a <span className="text-primary">Conversation</span>
            </h2>
            <p className="text-lg text-secondary font-default leading-relaxed max-w-md">
              Connect with Nova Project Management to streamline your global operations. Our experts are ready to handle the complexities of your next large-scale project.
            </p>
          </div>

          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-none bg-primary/5 flex items-center justify-center shrink-0 border border-primary/10">
                <Building2 className="size-6 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-default font-semibold text-foreground mb-1">Global Operations</h4>
                <p className="text-secondary font-default">Specialized administration and logistics solutions for multi-national enterprises.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="size-12 rounded-none bg-primary/5 flex items-center justify-center shrink-0 border border-primary/10">
                <Mail className="size-6 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-default font-semibold text-foreground mb-1">Direct Contact</h4>
                <p className="text-secondary font-mono text-xl">solutions@novaprojectmgmt.com</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="size-12 rounded-none bg-primary/5 flex items-center justify-center shrink-0 border border-primary/10">
                <MapPin className="size-6 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-default font-semibold text-foreground mb-1">Global Headquarters</h4>
                <p className="text-secondary font-default">London, United Kingdom</p>
              </div>
            </div>
          </div>
        </div>

        {}
        <Card className="border-border/20 shadow-4xl shadow-black/20 bg-card/30 backdrop-blur-md rounded-none">
          <CardHeader className="pb-8 border-b border-border/10">
            <CardTitle className="text-2xl font-default font-bold">Project Inquiry</CardTitle>
            <CardDescription className="text-base font-default text-secondary">
              Submit your project details and our coordination team will reach out within 24 hours.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formKey="conversation_form_connect" formId="conversation_form_connect" sectionName="Start a Conversation" defaultValues={{
            name: "",
            email: "",
            company_name: "",
            phone: "",
            partnership_type: "",
            message: ""
          }} submitText="Initiate Consultation" successMessage="Your inquiry has been successfully transmitted to our global operations team. A project coordinator will contact you shortly.">
              <PartnerForm />
            </FormProvider>
          </CardContent>
        </Card>

      </div>
    </section>;
}